import PropTypes from 'prop-types';

/**
 * One window: which days, and between what times.
 *
 * EXTRACTED FROM AdminNpcs.js RATHER THAN WRITTEN AGAIN, because two pages ask
 * the same question and a second copy is the one that goes stale — the original
 * would keep the overnight-window explanation and the copy would quietly lose
 * it, which is how two screens end up disagreeing about what 22:00 to 02:00
 * means.
 *
 * ONE WINDOW, AND THE CALLER DECIDES HOW MANY. A character is out or they are
 * not, so AdminNpcs renders exactly one. A shop keeps different hours on
 * Saturday and shuts for lunch, so AdminBusinesses renders a list with add and
 * remove — the same shape ZoneInspector uses for a zone's doors.
 *
 * `onChange` RECEIVES THE WHOLE NEXT WINDOW, not a patch. AdminNpcs holds these
 * three fields flattened into its one big `draft` object and AdminBusinesses
 * holds a list of them; handing back a patch would make the flat caller merge
 * something it has no obvious place to merge into, and getting that wrong is
 * silent — the buttons still highlight, the save just does not carry them.
 */

/** 0 = Sunday, matching `Date.getDay()` and the server's `WEEKDAY_INDEX`. */
export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ScheduleWindow({ value, onChange, idPrefix }) {
  const days = value.days || [];

  const toggle = (day) => onChange({
    ...value,
    days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day],
  });

  return (
    <>
      <div className="flex flex-wrap gap-1">
        {DAY_LABELS.map((label, day) => (
          <button
            key={label}
            type="button"
            onClick={() => toggle(day)}
            className={`rounded px-2 py-1 text-xs ${
              days.includes(day)
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
        <span className="self-center pl-2 text-xs text-gray-500">
          {days.length === 0 ? 'every day' : ''}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2 text-sm">
        <span className="text-gray-600 dark:text-gray-400">From</span>
        <input
          aria-label="Window starts at"
          id={`${idPrefix}-from`}
          type="time"
          value={value.from || ''}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
          className="rounded border border-gray-300 px-2 py-1 dark:border-gray-700 dark:bg-gray-900"
        />
        <span className="text-gray-600 dark:text-gray-400">to</span>
        <input
          aria-label="Window ends at"
          id={`${idPrefix}-to`}
          type="time"
          value={value.to || ''}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
          className="rounded border border-gray-300 px-2 py-1 dark:border-gray-700 dark:bg-gray-900"
        />
      </div>
    </>
  );
}

ScheduleWindow.propTypes = {
  value: PropTypes.shape({
    days: PropTypes.arrayOf(PropTypes.number),
    from: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  /** Called with the WHOLE next window, never a patch. */
  onChange: PropTypes.func.isRequired,
  /** Keeps input ids unique when a page renders several of these. */
  idPrefix: PropTypes.string,
};

ScheduleWindow.defaultProps = { idPrefix: 'window' };

export default ScheduleWindow;
